import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSession } from "providers/SessionProvider";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import {
  buildObservationRecordItemsFromStockUnits,
  buildObservationSummaryFromStockUnits,
  type ArticleLocationObservationRecordItem,
} from "helpers/articleLocationObservationRecords";
import { queryBuilder } from "helpers/query";
import { axiosGet } from "api";
import {
  ARTICLE_LOCATIONS_EXPANDED_EXPORT_URL,
  ARTICLE_LOCATIONS_URL,
} from "constants/urls";
import {
  ArticleLocation,
  ArticleLocationExportResponse,
  ArticleLocationObservationRecordStockUnit,
  Client,
  IMovementsQuery,
  StockExportGroupBy,
  StockStatusFilter,
  UpdateArticleLocationObservationRecordsPayload,
} from "types";
import CustomGrid from "components/lists/CustomGrid";
import { FaFileDownload } from "react-icons/fa";
import { buildExcelHeader } from "helpers/exportToExcel";
import { Roles, STATUS_CODES } from "constants/index";
import { MdVisibility } from "react-icons/md";
import Loader from "components/lists/Loader";
import ObservationRecordsModal from "components/utils/ObservationRecordsModal";
import {
  getArticleLocationQuantityByStatus,
  getStockStatusOption,
  stockStatusOptions,
} from "helpers/stockStatus";
import StockExportGroupingModal from "./StockExportGroupingModal";
import {
  exportGroupedStockToExcel,
  exportGroupedStockToPdf,
  exportStockToExcel,
  exportStockToPdf,
  StockExportColumn,
} from "helpers/stockExports";

export default function StockList() {
  const {
    data: articleLocations,
    fetchData: getArticleLocations,
    loading: articleLocationsLoading,
    setData: setArticleLocations,
  } = useCrud<ArticleLocation>(`${ARTICLE_LOCATIONS_URL}expanded/`);
  const { fetchData: getObservationRecordStockUnits } =
    useCrud<ArticleLocationObservationRecordStockUnit>(ARTICLE_LOCATIONS_URL, {
      disableAutoFetch: true,
    });
  const { updateData: updateObservationRecordStockUnits } =
    useCrud<UpdateArticleLocationObservationRecordsPayload>(ARTICLE_LOCATIONS_URL, {
      disableAutoFetch: true,
    });

  const { stores, clients, locations, articles, groups, userData } =
    useSession();
  const { showSnackBar } = useToast();
  const isClientUser = userData?.role === Roles.CLIENT;
  const isOperatorUser = userData?.role === Roles.OPERATOR;

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const [filters, setFilters] = useState({
    storeIds: [] as number[],
    locationFrom: undefined as { id: number; label: string } | undefined,
    locationTo: undefined as { id: number; label: string } | undefined,
    locationIds: [] as number[],
    clientIds: [] as number[],
    lineIds: [] as number[],
    articleCode: "",
    articleDescription: "",
    lote: "",
    oc: "",
    amount_per_container: "",
    fecha_vencimiento: "",
    fecha_ingreso: "",
    groupId: undefined as number | undefined,
    status: "all" as StockStatusFilter,
    movementId: "",
  });

  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [selectedLineClients, setSelectedLineClients] = useState<Client[]>([]);

  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState(false);
  const [savingObservationRecordId, setSavingObservationRecordId] = useState<
    string | null
  >(null);
  const [observationRecordsOpen, setObservationRecordsOpen] = useState(false);
  const [observationRecords, setObservationRecords] = useState<
    ArticleLocationObservationRecordItem[]
  >([]);
  const [selectedObservationRow, setSelectedObservationRow] =
    useState<ArticleLocation | null>(null);
  const isObservationReadOnly =
    isOperatorUser || selectedObservationRow?.status === "in_work_orders";
  const [movementIdInput, setMovementIdInput] = useState("");
  const [articleDescriptionInput, setArticleDescriptionInput] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState<
    "excel" | "pdf" | null
  >(null);

  const uniqueLotes = useMemo(
    () =>
      Array.from(new Set(articleLocations.map((a) => a.lote).filter(Boolean))),
    [articleLocations],
  );
  const uniqueOCs = useMemo(
    () =>
      Array.from(new Set(articleLocations.map((a) => a.oc).filter(Boolean))),
    [articleLocations],
  );
  const uniqueAmountPerContainer = useMemo(
    () =>
      Array.from(
        new Set(
          articleLocations
            .map((a) => String(a.amount_per_container))
            .filter((v) => v != null && v !== "")
            .map((v) => String(v)),
        ),
      ),
    [articleLocations],
  );
  const uniqueVencimientos = useMemo(
    () =>
      Array.from(
        new Set(
          articleLocations.map((a) => a.fecha_vencimiento).filter(Boolean),
        ),
      ),
    [articleLocations],
  );
  const uniqueIngresos = useMemo(
    () =>
      Array.from(
        new Set(articleLocations.map((a) => a.fecha_ingreso).filter(Boolean)),
      ),
    [articleLocations],
  );
  const lineOptions = useMemo(
    () => {
      if (isClientUser) {
        const assignedClient = clients.find(
          (client) => client.id === userData?.client_id,
        );

        if (!assignedClient || assignedClient.parent_client_id != null) {
          return [];
        }

        return (assignedClient.child_clients ?? []).filter(
          (client) => client.is_active,
        );
      }

      return clients.filter(
        (client) =>
          client.is_active &&
          client.parent_client_id != null &&
          filters.clientIds.includes(client.parent_client_id),
      );
    },
    [clients, filters.clientIds, isClientUser, userData?.client_id],
  );

  const queryClientIds = useMemo(
    () => (filters.lineIds.length > 0 ? filters.lineIds : filters.clientIds),
    [filters.clientIds, filters.lineIds],
  );

  const scopedClientIds = useMemo(
    () => [
      ...queryClientIds,
      ...clients
        .filter((client) =>
          queryClientIds.includes(client.parent_client_id ?? 0),
        )
        .map((client) => client.id),
    ],
    [clients, queryClientIds],
  );

  const visibleArticleCodes = useMemo(
    () => new Set(articleLocations.map((row) => row.article.code)),
    [articleLocations],
  );

  const uniqueArticles = useMemo(() => {
    const scopedArticles =
      queryClientIds.length > 0
        ? articles.filter((article) =>
            scopedClientIds.includes(article.client_id),
          )
        : articles;

    const visibleArticles = isClientUser
      ? scopedArticles.filter((article) =>
          visibleArticleCodes.has(article.code),
        )
      : scopedArticles;

    return Array.from(
      new Map(
        visibleArticles.map((article) => [article.code, article]),
      ).values(),
    );
  }, [
    articles,
    isClientUser,
    queryClientIds,
    scopedClientIds,
    visibleArticleCodes,
  ]);

  const groupOptions = useMemo(
    () =>
      groups.filter(
        (group) =>
          queryClientIds.length === 0 ||
          scopedClientIds.includes(group.client_id || 0),
      ),
    [groups, queryClientIds, scopedClientIds],
  );

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      groupId: undefined,
      articleCode: "",
    }));
  }, [queryClientIds]);

  const buildStockDetailQuery = () =>
    queryBuilder({
      storeIds: filters.storeIds,
      locationIds: filters.locationIds,
      clientIds: queryClientIds,
      articleCode: filters.articleCode || undefined,
      articleDescription: filters.articleDescription.trim() || undefined,
      movementId: filters.movementId || undefined,
      lote: filters.lote || undefined,
      oc: filters.oc || undefined,
      amount_per_container: filters.amount_per_container || undefined,
      fecha_vencimiento: filters.fecha_vencimiento || undefined,
      fecha_ingreso: filters.fecha_ingreso || undefined,
      groupId: filters.groupId,
      status: filters.status,
    } as IMovementsQuery);

  const buildStockDetailExportQuery = (groupBy: StockExportGroupBy) => {
    const query = buildStockDetailQuery();
    const separator = query.includes("?") ? "&" : "?";

    return `${query}${separator}groupBy=${groupBy}&_exportTimestamp=${Date.now()}`;
  };

  const handleSearch = async () => {
    const query = buildStockDetailQuery();

    await getArticleLocations(query, true);
  };

  useEffect(() => {
    handleSearch();
  }, [
    filters.storeIds,
    filters.locationFrom,
    filters.locationTo,
    filters.locationIds,
    filters.clientIds,
    filters.lineIds,
    filters.articleCode,
    filters.articleDescription,
    filters.lote,
    filters.oc,
    filters.fecha_vencimiento,
    filters.fecha_ingreso,
    filters.amount_per_container,
    filters.groupId,
    filters.status,
    filters.movementId,
  ]);

  const buildStockExportColumns = (): StockExportColumn<ArticleLocation>[] => {
    const columns: StockExportColumn<ArticleLocation>[] = [];

    if (userData?.role !== Roles.CLIENT) {
      columns.push({
        id: "store",
        header: "Depósito",
        value: (a) => a.location?.store?.description ?? "-",
        pdfCellWidth: 15,
      });
      columns.push({
        id: "location",
        header: "Ubicación.",
        value: (a) => a.location?.code ?? "-",
        pdfCellWidth: 14,
      });
    }

    columns.push({
      id: "article",
      header: "Código",
      value: (a) => a.article?.code ?? "-",
      pdfCellWidth: 20,
    });
    columns.push({
      id: "description",
      header: "Desc.",
      value: (a) => a.article?.description ?? "-",
      pdfCellWidth: 32,
    });

    columns.push({
      id: "client",
      header: "Cliente",
      value: (a) => a.article?.client?.code ?? "-",
      pdfCellWidth: 10,
    });

    if (filterVisibility.show_lote)
      columns.push({
        id: "lote",
        header: "Lote",
        value: (a) => a.lote ?? "-",
        pdfCellWidth: 18,
      });

    if (filterVisibility.show_oc)
      columns.push({
        id: "oc",
        header: "OC",
        value: (a) => a.oc ?? "-",
        pdfCellWidth: 16,
      });

    if (filterVisibility.show_fecha_vencimiento)
      columns.push({
        id: "fecha_vencimiento",
        header: "F. Venc.",
        value: (a) => a.fecha_vencimiento ?? "-",
        pdfCellWidth: 16,
      });

    columns.push(
      {
        id: "fecha_ingreso",
        header: "F. Ing.",
        value: (a) => a.fecha_ingreso ?? "-",
        pdfCellWidth: 16,
      },
      {
        id: "status",
        header: "Estado",
        value: (a) => a.status_label ?? "-",
        pdfCellWidth: 16,
      },
      {
        id: "quantity",
        header: "Cant.",
        value: (a) =>
          a.status_quantity ?? getArticleLocationQuantityByStatus(a, filters.status),
        pdfCellWidth: 10,
      },
      {
        id: "amount_per_container",
        header: "Cant. x Cont.",
        value: (a) => a.amount_per_container ?? "-",
        pdfCellWidth: 14,
      },
      {
        id: "saldo",
        header: "Saldo",
        value: (a) =>
          a.saldo ??
          Number(
            a.status_quantity ?? getArticleLocationQuantityByStatus(a, filters.status),
          ) * Number(a.amount_per_container ?? 0),
        pdfCellWidth: 10,
      },
      {
        id: "unit",
        header: "UM",
        value: (a) => a.article?.unit?.description ?? "-",
        pdfCellWidth: 13,
      },
      {
        id: "presentation",
        header: "Presentación",
        value: (a) => a.article?.presentation ?? "-",
        pdfCellWidth: 14,
      },
      {
        id: "observation",
        header: "Observación",
        value: (a) => a.observation ?? "-",
        pdfCellWidth: 28,
      },
    );

    return columns;
  };

  const getStockExportSubtitle = () =>
    queryClientIds.length === 0
      ? "Clientes: " +
        (userData?.role === Roles.CLIENT
          ? `${userData?.client?.code} - ${userData?.client?.razon_social}`
          : "TODOS")
      : `Clientes: ${clients
          .filter((c) => queryClientIds.includes(c.id))
          .map((c) => `${c.code} - ${c.razon_social}`)
          .join(", ")}`;

  const openExportModal = (format: "excel" | "pdf") => {
    setPendingExportFormat(format);
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    if (exportLoading) return;
    setExportModalOpen(false);
    setPendingExportFormat(null);
  };

  const handleExport = async (groupBy: StockExportGroupBy) => {
    if (!pendingExportFormat) return;

    const { headerRows } = buildExcelHeader(clients, filters, userData);

    try {
      setExportLoading(true);
      const response = await axiosGet(
        `${ARTICLE_LOCATIONS_EXPANDED_EXPORT_URL}${buildStockDetailExportQuery(
          groupBy,
        )}`,
        true,
      );

      if (response.status !== STATUS_CODES.OK) {
        throw new Error(
          response.data?.message ?? "No se pudo obtener el stock para exportar.",
        );
      }

      const exportData = response.data as ArticleLocationExportResponse;
      const columns = buildStockExportColumns();

      if (pendingExportFormat === "excel") {
        if (groupBy === "none") {
          exportStockToExcel({
            rows: exportData.rows ?? [],
            columns,
            sheetName: "Stock",
            fileNamePrefix: "Stock",
            headerRows,
          });
        } else {
          exportGroupedStockToExcel({
            sections: exportData.sections ?? [],
            columns,
            groupBy,
            sheetName: "Stock",
            fileNamePrefix: "Stock",
            headerRows,
          });
        }
      } else {
        if (groupBy === "none") {
          exportStockToPdf({
            rows: exportData.rows ?? [],
            columns,
            title: "Listado de Stock",
            subtitle: getStockExportSubtitle(),
            fileNamePrefix: "Stock",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        } else {
          exportGroupedStockToPdf({
            sections: exportData.sections ?? [],
            columns,
            groupBy,
            title: "Listado de Stock",
            subtitle: getStockExportSubtitle(),
            fileNamePrefix: "Stock",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        }
      }

      setExportModalOpen(false);
      setPendingExportFormat(null);
    } catch (error) {
      console.error(error);
      showSnackBar(
        error instanceof Error
          ? error.message
          : "No se pudo preparar el stock para exportar.",
        "error",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const filterVisibility = useMemo(() => {
    if (selectedClients.length === 0) {
      return {
        show_lote: true,
        show_oc: true,
        show_fecha_vencimiento: true,
      };
    }

    return {
      show_lote: selectedClients.some((c) => c.show_lote),
      show_oc: selectedClients.some((c) => c.show_oc),
      show_fecha_vencimiento: selectedClients.some(
        (c) => c.show_fecha_vencimiento,
      ),
    };
  }, [selectedClients]);

  const fetchObservationRecordStockUnits = useCallback(
    async (row: ArticleLocation) => {
      const rowStatus = row.status;

      if (
        rowStatus !== "available" &&
        rowStatus !== "reserved" &&
        rowStatus !== "not_available" &&
        rowStatus !== "in_work_orders"
      ) {
        throw new Error("La fila seleccionada no tiene un estado válido.");
      }

      const params: Record<string, string | number> = { status: rowStatus };

      if (rowStatus === "reserved") {
        if (!Number(row.reserved_movement_id ?? 0)) {
          throw new Error(
            "La fila reservada seleccionada no tiene reserved_movement_id.",
          );
        }

        params.reserved_movement_id = Number(row.reserved_movement_id);
      }

      const response = await getObservationRecordStockUnits(
        `${row.id}/observation-records`,
        true,
        params,
      );

      if (response.status !== STATUS_CODES.OK) {
        throw new Error(
          response.data?.message ??
            "No se pudieron obtener las observaciones del stock.",
        );
      }

      return (response.data ?? []) as ArticleLocationObservationRecordStockUnit[];
    },
    [getObservationRecordStockUnits],
  );

  const handleOpenObservationRecords = useCallback(
    async (row: ArticleLocation) => {
      setSelectedObservationRow(row);
      setObservationRecordsOpen(true);
      setObservationRecordsLoading(true);

      try {
        const stockUnits = await fetchObservationRecordStockUnits(row);
        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(stockUnits),
        );
      } catch (error) {
        console.error(error);
        setObservationRecords([]);
        showSnackBar(
          error instanceof Error
            ? error.message
            : "No se pudieron cargar las observaciones.",
          "error",
        );
      } finally {
        setObservationRecordsLoading(false);
      }
    },
    [fetchObservationRecordStockUnits, showSnackBar],
  );

  const handleCloseObservationRecords = useCallback(() => {
    setObservationRecordsOpen(false);
    setSelectedObservationRow(null);
    setObservationRecords([]);
    setObservationRecordsLoading(false);
    setSavingObservationRecordId(null);
  }, []);

  const handleSaveObservationRecord = useCallback(
    async ({
      recordId,
      observation,
      quantityMode,
      specificQuantity,
    }: {
      recordId: string;
      observation: string;
      quantityMode: "total" | "specific";
      specificQuantity: number | null;
    }) => {
      if (isObservationReadOnly || !selectedObservationRow) {
        return;
      }

      const rowStatus = selectedObservationRow.status;

      if (
        rowStatus !== "available" &&
        rowStatus !== "reserved" &&
        rowStatus !== "not_available"
      ) {
        showSnackBar("La fila seleccionada no tiene un estado válido.", "error");
        return;
      }

      const targetRecord = observationRecords.find(
        (record) => record.id === recordId,
      );

      if (!targetRecord) {
        showSnackBar(
          "No se encontró el grupo de observaciones seleccionado.",
          "error",
        );
        return;
      }

      const targetQuantity =
        quantityMode === "specific"
          ? Number(specificQuantity ?? 0)
          : targetRecord.stock_unit_ids.length;

      if (
        !Number.isFinite(targetQuantity) ||
        targetQuantity <= 0 ||
        targetQuantity > targetRecord.stock_unit_ids.length
      ) {
        showSnackBar("La cantidad específica seleccionada no es válida.", "error");
        return;
      }

      if (
        rowStatus === "reserved" &&
        !Number(selectedObservationRow.reserved_movement_id ?? 0)
      ) {
        showSnackBar(
          "La fila reservada seleccionada no tiene reserved_movement_id.",
          "error",
        );
        return;
      }

      const selectedStockUnitIds = targetRecord.stock_unit_ids.slice(
        0,
        targetQuantity,
      );
      const normalizedObservation = observation.trim();

      setSavingObservationRecordId(recordId);

      try {
        const response = await updateObservationRecordStockUnits(
          `${selectedObservationRow.id}/observation-records`,
          {
            status: rowStatus,
            stock_unit_ids: selectedStockUnitIds,
            observation:
              normalizedObservation.length > 0 ? normalizedObservation : null,
            reserved_movement_id:
              rowStatus === "reserved"
                ? Number(selectedObservationRow.reserved_movement_id)
                : null,
          },
          true,
          false,
        );

        if (response.status !== STATUS_CODES.OK) {
          throw new Error(
            response.data?.message ??
              "No se pudieron actualizar las observaciones del stock.",
          );
        }

        const updatedStockUnits = (response.data ??
          []) as ArticleLocationObservationRecordStockUnit[];

        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(updatedStockUnits),
        );

        const nextObservationSummary =
          buildObservationSummaryFromStockUnits(updatedStockUnits);

        setArticleLocations((currentRows) =>
          currentRows.map((currentRow) =>
            currentRow.row_key === selectedObservationRow.row_key
              ? { ...currentRow, observation: nextObservationSummary }
              : currentRow,
          ),
        );

        showSnackBar("Se actualizaron las observaciones correctamente.", "success");
      } catch (error) {
        console.error(error);
        showSnackBar(
          error instanceof Error
            ? error.message
            : "No se pudieron actualizar las observaciones.",
          "error",
        );
      } finally {
        setSavingObservationRecordId(null);
      }
    },
    [
      isObservationReadOnly,
      observationRecords,
      selectedObservationRow,
      setArticleLocations,
      showSnackBar,
      updateObservationRecordStockUnits,
    ],
  );

  const structure = useMemo(() => {
    const fields: unknown[] = [];
    if (userData?.role !== Roles.CLIENT) {
      fields.push({
        field: "deposito",
        headerName: "Depósito",
        minWidth: 160,
        flex: 1,
        valueGetter: (p: any) => p.row.location?.store?.description ?? "-",
      });
      fields.push({
        field: "ubicacion",
        headerName: "Ubicación",
        minWidth: 140,
        flex: 1,
        valueGetter: (p: any) => p.row.location?.code ?? "-",
      });
    }
    return {
      name: "articleLocations",
      labelName: "Stock",
      fields: fields
        .concat([
          {
            field: "codigo",
            headerName: "Código",
            minWidth: 120,
            flex: 1,
            valueGetter: (p: any) => p.row.article?.code ?? "-",
          },
          {
            field: "descripcion",
            headerName: "Descripción",
            minWidth: 240,
            flex: 1.5,
            valueGetter: (p: any) => p.row.article?.description ?? "-",
          },
          {
            field: "cliente",
            headerName: "Cliente",
            minWidth: 140,
            flex: 0.8,
            valueGetter: (p: any) => p.row.article?.client?.code ?? "-",
          },
          filterVisibility.show_lote && {
            field: "lote",
            headerName: "Lote",
            minWidth: 140,
            flex: 1,
            valueGetter: (p: any) => p.row.lote ?? "-",
          },
          filterVisibility.show_oc && {
            field: "oc",
            headerName: "OC",
            minWidth: 120,
            flex: 1,
            valueGetter: (p: any) => p.row.oc ?? "-",
          },
          filterVisibility.show_fecha_vencimiento && {
            field: "fecha_vencimiento",
            headerName: "Fecha Vencimiento",
            minWidth: 180,
            flex: 1,
            valueGetter: (p: any) => p.row.fecha_vencimiento ?? "-",
          },
          {
            field: "fecha_ingreso",
            headerName: "Fecha Ingreso",
            minWidth: 150,
            flex: 1,
            valueGetter: (p: any) => p.row.fecha_ingreso ?? "-",
          },
          {
            field: "status_label",
            headerName: "Estado de mercadería",
            minWidth: 190,
            flex: 1,
            valueGetter: (p: any) => p.row.status_label ?? "-",
          },
          {
            field: "stock",
            headerName: "Cant.",
            minWidth: 100,
            flex: 0.6,
            valueGetter: (p: any) =>
              getArticleLocationQuantityByStatus(p.row, filters.status),
          },
          {
            field: "cantidad_contenedor",
            headerName: "Cant. por Contenedor",
            minWidth: 190,
            flex: 1,
            valueGetter: (p: any) => p.row.amount_per_container ?? "-",
          },
          {
            field: "unidad",
            headerName: "Unidad de Medida",
            minWidth: 170,
            flex: 1,
            valueGetter: (p: any) => p.row.article?.unit?.description ?? "-",
          },
          {
            field: "presentation",
            headerName: "Presentación",
            minWidth: 150,
            flex: 1,
            valueGetter: (p: any) => p.row.article?.presentation ?? "-",
          },
          {
            field: "observation",
            headerName: "Observación",
            width: 110,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params: any) => {
              return (
                <Tooltip title={isOperatorUser ? "Ver observaciones" : "Ver y editar observaciones"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={observationRecordsLoading}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleOpenObservationRecords(params.row);
                      }}
                    >
                      <MdVisibility fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
        ])
        .filter(Boolean) as GridColDef[],
    };
  }, [
    filterVisibility,
    filters.status,
    handleOpenObservationRecords,
    isOperatorUser,
    observationRecordsLoading,
    userData,
  ]);

  useEffect(() => {
    if (filters.locationFrom && !filters.locationTo) {
      const storeLocations = locations.filter((l) =>
        filters.storeIds.includes(l.store_id),
      );

      const fromIndex = storeLocations.findIndex(
        (l) => l.id === filters.locationFrom?.id,
      );

      if (fromIndex === -1) return;

      const selectedRange = storeLocations.slice(fromIndex);

      setFilters((prev) => ({
        ...prev,
        locationIds: selectedRange.map((l) => l.id),
      }));
    }
  }, [filters.locationFrom, filters.storeIds, locations]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <Grid container columnSpacing={2} rowSpacing={1} my={1}>
            {userData?.role !== Roles.CLIENT && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  multiple
                  options={[
                    { id: 0, label: "Todos" },
                    ...stores
                      .filter((s) => s.is_active)
                      .map((s) => ({ id: s.id, label: s.description })),
                  ]}
                  onChange={(_, value) => {
                    if (value.some((v) => v.id === 0)) {
                      const allStoreIds = stores
                        .filter((s) => s.is_active)
                        .map((s) => s.id);
                      setFilters((prev) => ({
                        ...prev,
                        storeIds: allStoreIds,
                      }));
                    } else {
                      setFilters((prev) => ({
                        ...prev,
                        storeIds: value.map((v) => v.id),
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Depósitos" />
                  )}
                  disabled={articleLocationsLoading}
                />
              </Grid>
            )}

            {userData?.role !== Roles.CLIENT && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  disabled={!filters.storeIds.length || articleLocationsLoading}
                  options={locations
                    .filter((l) => filters.storeIds.includes(l.store_id))
                    .map((l) => ({ id: l.id, label: l.code }))}
                  onChange={(_, value) => {
                    setFilters((prev) => ({
                      ...prev,
                      locationFrom: value ?? undefined,
                      locationTo: undefined,
                      locationIds: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Desde" />
                  )}
                />
              </Grid>
            )}

            {userData?.role !== Roles.CLIENT && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  disabled={
                    !filters.storeIds.length ||
                    !filters.locationFrom ||
                    articleLocationsLoading
                  }
                  options={locations
                    .filter((l) => filters.storeIds.includes(l.store_id))
                    .map((l) => ({ id: l.id, label: l.code }))}
                  onChange={(_, value) => {
                    if (!value || !filters.locationFrom) return;

                    const storeLocations = locations.filter((l) =>
                      filters.storeIds.includes(l.store_id),
                    );

                    const fromIndex = storeLocations.findIndex(
                      (l) => l.id === filters.locationFrom?.id,
                    );
                    const toIndex = storeLocations.findIndex(
                      (l) => l.id === value.id,
                    );

                    if (fromIndex === -1 || toIndex === -1) return;

                    const [start, end] =
                      fromIndex <= toIndex
                        ? [fromIndex, toIndex]
                        : [toIndex, fromIndex];

                    const selectedRange = storeLocations.slice(start, end + 1);

                    setFilters((prev) => ({
                      ...prev,
                      locationTo: value,
                      locationIds: selectedRange.map((l) => l.id),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Hasta" />
                  )}
                />
              </Grid>
            )}

            {userData?.role !== Roles.CLIENT && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  multiple
                  options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
                    ...c,
                    id: c.id,
                    label: `${c.code} - ${c.razon_social}`,
                  }))}
                  onChange={(_, value) => {
                    setSelectedClients(value);
                    setSelectedLineClients([]);
                    setFilters((prev) => ({
                      ...prev,
                      clientIds: value.map((v) => v.id),
                      lineIds: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" />
                  )}
                  disabled={articleLocationsLoading}
                />
              </Grid>
            )}

            {(!isClientUser || lineOptions.length > 0) && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  multiple
                  options={lineOptions}
                  value={selectedLineClients}
                  getOptionLabel={(option) => `${option.code} - ${option.razon_social}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => {
                    setSelectedLineClients(value);
                    setFilters((prev) => ({
                      ...prev,
                      lineIds: value.map((v) => v.id),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Líneas" />
                  )}
                  disabled={
                    (!isClientUser && filters.clientIds.length === 0) ||
                    lineOptions.length === 0 ||
                    articleLocationsLoading
                  }
                />
              </Grid>
            )}

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={groupOptions}
                getOptionLabel={(option) => option.description ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter((o) =>
                    o.description
                      ?.toLowerCase()
                      .includes(state.inputValue.toLowerCase()),
                  )
                }
                onChange={(_, value) =>
                  setFilters((prev) => ({ ...prev, groupId: value?.id }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Grupo de artículos" />
                )}
                disabled={articleLocationsLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                disableClearable
                options={stockStatusOptions}
                value={getStockStatusOption(filters.status)}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: value?.id ?? "all",
                  }))
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Estado de mercadería" />
                )}
                disabled={articleLocationsLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={uniqueArticles}
                value={
                  uniqueArticles.find(
                    (article) => article.code === filters.articleCode,
                  ) ?? null
                }
                getOptionLabel={(option) => option.code ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter((o) =>
                    o.code
                      ?.toLowerCase()
                      .includes(state.inputValue.toLowerCase()),
                  )
                }
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    articleCode: value?.code ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Artículo" />
                )}
                disabled={articleLocationsLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={uniqueAmountPerContainer.map((v) => ({
                  label: String(v),
                }))}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    amount_per_container: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Cant. x Contenedor" />
                )}
                disabled={articleLocationsLoading}
              />
            </Grid>

            {filterVisibility.show_lote && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  options={uniqueLotes.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({
                      ...prev,
                      lote: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Lote" />
                  )}
                  disabled={articleLocationsLoading}
                />
              </Grid>
            )}

            {filterVisibility.show_oc && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  options={uniqueOCs.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({ ...prev, oc: value?.label ?? "" }))
                  }
                  renderInput={(params) => <TextField {...params} label="OC" />}
                  disabled={articleLocationsLoading}
                />
              </Grid>
            )}

            {filterVisibility.show_fecha_vencimiento && (
              <Grid item md={3} sm={6} xs={12}>
                <Autocomplete
                  options={uniqueVencimientos.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({
                      ...prev,
                      fecha_vencimiento: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Fecha Vencimiento" />
                  )}
                  disabled={articleLocationsLoading}
                />
              </Grid>
            )}

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={uniqueIngresos.map((v) => ({ label: v }))}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    fecha_ingreso: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Fecha Ingreso" />
                )}
                disabled={articleLocationsLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <TextField
                label="Número de Ingreso"
                type="text"
                fullWidth
                value={movementIdInput}
                onChange={(e) => setMovementIdInput(e.target.value)}
                onBlur={() =>
                  setFilters((prev) => ({
                    ...prev,
                    movementId: movementIdInput.trim(),
                  }))
                }
                disabled={articleLocationsLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <TextField
                label="Descripción de Artículo"
                type="text"
                fullWidth
                value={articleDescriptionInput}
                onChange={(e) => setArticleDescriptionInput(e.target.value)}
                onBlur={() =>
                  setFilters((prev) => ({
                    ...prev,
                    articleDescription: articleDescriptionInput.trim(),
                  }))
                }
                disabled={articleLocationsLoading}
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Stock por Detalle</Typography>

            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FaFileDownload />}
                    onClick={() => openExportModal("excel")}
                    disabled={articleLocations.length === 0 || exportLoading}
                  >
                    Exportar Excel
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<FaFileDownload />}
                    onClick={() => openExportModal("pdf")}
                    disabled={articleLocations.length === 0 || exportLoading}
                  >
                    Exportar PDF
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {articleLocationsLoading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<ArticleLocation>
              structure={structure}
              rows={articleLocations}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection={false}
              getRowId={(row) => row.row_key ?? row.id}
            />
          )}

          {!articleLocationsLoading && articleLocations.length === 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                No hay resultados disponibles
              </Typography>
            </Alert>
          )}
        </Paper>
      </Grid>
      <ObservationRecordsModal
        open={observationRecordsOpen}
        loading={observationRecordsLoading}
        savingRecordId={savingObservationRecordId}
        readOnly={isObservationReadOnly}
        records={observationRecords}
        onClose={handleCloseObservationRecords}
        onSaveRecord={isObservationReadOnly ? undefined : handleSaveObservationRecord}
        subtitle={
          selectedObservationRow
            ? `${selectedObservationRow.article?.code ?? "-"} - ${
                selectedObservationRow.article?.description ?? "-"
              } | Ubicación ${selectedObservationRow.location?.code ?? "-"} | Estado ${
                selectedObservationRow.status_label ?? "-"
              }`
            : null
        }
      />
      <StockExportGroupingModal
        open={exportModalOpen}
        loading={exportLoading}
        allowUngrouped
        onClose={closeExportModal}
        onConfirm={handleExport}
      />
      <Loader isFirstLoad={false} isLoading={exportLoading} />
    </Grid>
  );
}
